import joi from 'joi';
import { Box, Button, Dialog, DialogContent, TextField, Tooltip } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { Feature } from '@loyalty/shared-common';
import { QlfFormRow, useAlerts } from '@qualifio/ui-commons';
import { makeStyles } from '@mui/styles';
import { useCallback, useState } from 'react';
import { useLeaverPrompt } from '@qualifio/ui-commons/dist/components/qlf-prompt';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styles from './TestUserCreationDialog.module.scss';
import { Loader } from '../shared/layout/Loader';
import { Title } from '../shared/layout/Title';
import { useAccounts } from '../accounts/AccountsContext';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useFeatures } from '../features/FeaturesContext';
import { useJoiResolver } from '../shared/forms/use-joi-resolver';
import { useTestUsers } from './TestUsersContext';

const useStyles = makeStyles(() => ({
  formFields: {
    marginLeft: '10px',
  },
}));

interface FormType {
  identityKind: string;
  identityValue: string;
  firstName: string;
  lastName: string;
}

const formSchema = joi.object<FormType>({
  identityKind: joi.string().valid('email').required(),
  identityValue: joi.string().email({ tlds: false }).required(),
  firstName: joi.string(),
  lastName: joi.string(),
});

export function TestUserCreationDialog({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { profileApi } = useApi();
  const { testUsers, setSelectedTestUser } = useTestUsers();
  const [isDuplicated, setIsDuplicated] = useState(false);
  const { features } = useFeatures();
  const { write: testUserWrite } = features[Feature.TEST_USERS];
  const [isCheckingForDuplicate, setIsCheckingForDuplicate] = useState(false);

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting },
    getValues,
    control,
  } = useForm<FormType>({
    mode: 'onChange',
    resolver: useJoiResolver(formSchema),
    defaultValues: {
      identityKind: 'email',
      identityValue: '',
    },
  });

  const { currentAccount } = useAccounts();
  const { fetchTestUsers } = useTestUsers();
  const { notify } = useAlerts();

  const { mutate, isPending } = useMutation({
    mutationKey: ['createTestUser', testUsers],

    mutationFn: async (data: FormType) => {
      setOpen(false);
      return await profileApi.upsertProfile(currentAccount.id, {
        isTestUser: true,
        email: data.identityValue,
        firstName: data.firstName,
        lastName: data.lastName,
        identities: [
          {
            kind: data.identityKind,
            value: data.identityValue,
          },
        ],
      });
    },

    onSuccess: data => {
      fetchTestUsers()
        .then(() => {
          setSelectedTestUser(data.profile);
        })
        .catch(error => {
          console.log(error);
          notify(t('test-users.fetch.error'), 'error');
        });
    },

    onError: error => {
      console.log(error);
      notify(t('test-users.create.error'), 'error');
    },
  });

  const checkForDuplicate = useCallback(() => {
    setIsCheckingForDuplicate(true);
    return profileApi
      .lookupProfileByIdentity(currentAccount.id, {
        kind: 'email',
        value: getValues('identityValue'),
      })
      .then(data => {
        setIsDuplicated(!!data);
        return !!data;
      })
      .finally(() => setIsCheckingForDuplicate(false))
      .catch(error => {
        console.error(error);
        notify(t('test-users.create.check-duplicate.error'), 'error');
        return false; // if it was duplicated, the next step will fail anyway, so let's not block the user for this error
      });
  }, [profileApi, currentAccount.id, getValues, notify, t]);

  const onSubmit = useCallback(
    (data: FormType) => {
      checkForDuplicate().then(duplicated => {
        if (!duplicated) {
          return mutate(data);
        }
      });
    },
    [mutate, checkForDuplicate],
  );

  useLeaverPrompt(isDirty || isSubmitting);

  return (
    <Dialog onClose={() => setOpen(false)} open={open} fullWidth={true} maxWidth="sm">
      <DialogContent className={styles.content}>
        <Box textAlign="center">
          <Title>{t('test-user.createDialog.title')}</Title>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.formFields}>
            <Controller
              name="firstName"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <QlfFormRow label={t('profiles.details.identity.properties.firstName')}>
                  <TextField
                    {...field}
                    error={!!error}
                    helperText={error?.message}
                    placeholder={t('profiles.details.identity.properties.firstName.placeholder')}
                  />
                </QlfFormRow>
              )}
            />

            <Controller
              name="lastName"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <QlfFormRow label={t('profiles.details.identity.properties.lastName')}>
                  <TextField
                    {...field}
                    error={!!error}
                    helperText={error?.message}
                    placeholder={t('profiles.details.identity.properties.lastName.placeholder')}
                  />
                </QlfFormRow>
              )}
            />

            <Controller
              name="identityKind"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <QlfFormRow label={t('test-users.create.fields.identityKind.label')}>
                  <TextField
                    {...field}
                    error={!!error}
                    helperText={error?.message}
                    placeholder={t('test-users.create.fields.identityKind.placeholder')}
                    disabled
                  />
                </QlfFormRow>
              )}
            />

            <Controller
              name="identityValue"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <QlfFormRow label={t('test-users.create.fields.identityValue.label')}>
                  <TextField
                    {...field}
                    error={!!error || isDuplicated}
                    helperText={
                      isDuplicated
                        ? t('test-users.create.field.identityValue.duplicated')
                        : error?.message
                    }
                    placeholder={t('test-users.create.fields.identityValue.placeholder')}
                    onChange={e => {
                      setIsDuplicated(false);
                      field.onChange(e);
                    }}
                  />
                </QlfFormRow>
              )}
            />
          </div>

          <Box display="flex" justifyContent="center" mt={1}>
            <Button type="button" color="grey" onClick={() => setOpen(false)}>
              {t('levels.configDialog.create.buttons.cancel')}
            </Button>
            <Tooltip title={testUserWrite ? '' : t('shared.action.permission.required')}>
              <div>
                <Button
                  type="submit"
                  color="success"
                  disabled={isCheckingForDuplicate}
                  className={styles.submit}
                >
                  {t('loyalty.dialog.confirm-button.submit')}
                  {(isPending || isCheckingForDuplicate) && <Loader />}
                </Button>
              </div>
            </Tooltip>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
}
